import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMercenary } from '../../contexts/MercenaryContext';
import axios from 'axios';
import InfirmaryBanner from '../Shared/InfirmaryBanner';
import styles from './Home.module.css';

interface PatchNote {
  id: number;
  title: string;
  content: string;
  version: string;
  date: string;
  author: string;
  createdAt: string;
}

const Home: React.FC = () => {
  const { socket } = useSocket();
  const { user } = useAuth();
  const { mercenary } = useMercenary();
  const [latestPatchNote, setLatestPatchNote] = useState<PatchNote | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Join socket room when user logs in
    if (socket && user?.id) {
      socket.emit('join_room', user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.id]);

  useEffect(() => {
    // Fetch latest patch note
    const fetchLatestPatchNote = async () => {
      try {
        const response = await axios.get('/api/tavern/patchnotes', { withCredentials: true });
        const patchNotes = response.data;
        if (patchNotes && patchNotes.length > 0) {
          // Get the most recent patch note
          setLatestPatchNote(patchNotes[0]);
        }
      } catch (error) {
        console.error('Error fetching patch notes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestPatchNote();
  }, []);

  if (!user) {
    return <p>Loading...</p>;
  }

  return (
    <div className={styles.homeContainer}>
      <h2 className={styles.welcomeTitle}>Welcome to Njordheim, {user.displayname ? user.displayname : user.username}!</h2>
      
      {/* Show infirmary banner if mercenary is hospitalized */}
      {mercenary && <InfirmaryBanner />}
      
      <div className={styles.contentGrid}>
        {/* Welcome section */}
        <div className={styles.welcomeSection}>
          <h3 className={styles.sectionTitle}>Join our discord!</h3>
          <p className={styles.welcomeText}>
            Join our community on Discord to connect with fellow mercenaries, share strategies, 
            and stay updated on the latest events and changes.
          </p>
          <a 
            href="https://discord.gg/ZR3HDBX29C" 
            target="_blank" 
            rel="noopener noreferrer" 
            className={styles.discordButton}
          >
            <svg className={styles.discordIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join Our Discord
          </a>
        </div>

        {/* Latest patch note section */}
        <div className={styles.patchNoteSection}>
          <h3 className={styles.sectionTitle}>Latest Update</h3>
          {loading ? (
            <p className={styles.loadingText}>Loading...</p>
          ) : latestPatchNote ? (
            <div className={styles.patchNoteCard}>
              <div className={styles.patchNoteHeader}>
                <h4 className={styles.patchNoteTitle}>{latestPatchNote.title}</h4>
                <span className={styles.patchNoteVersion}>{latestPatchNote.version}</span>
              </div>
              <p className={styles.patchNoteDate}>
                {new Date(latestPatchNote.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <div className={styles.patchNoteContent}>
                {latestPatchNote.content.slice(0, 300)}{latestPatchNote.content.length > 300 ? '...' : ''}
              </div>
              <Link to="/tavern?tab=chronicles" className={styles.viewAllLink}>
                View All Patch Notes →
              </Link>
            </div>
          ) : (
            <p className={styles.noPatchNotes}>No patch notes available yet.</p>
          )}
        </div>
      </div>

      {!mercenary && (
        <div className={styles.createMercenarySection}>
          <Link to="/create-mercenary" className={styles.createMercenaryButton}>
            Embark on Your Journey
          </Link>
        </div>
      )}
    </div>
  );
};

export default Home;
